import { jsx as _jsx } from "react/jsx-runtime";
import { useMemoizedContextProvider, useMemoizedContextSelector } from "@air/react-memoized-context";
import { createContext, useCallback, useContext, useMemo } from "react";
import { ASSET_MODAL_DISCUSSION_FILTERS, ASSET_MODAL_DISCUSSION_SORT, ASSET_MODAL_DISCUSSION_VERSION_FILTERS, defaultDiscussionPanelContextValue, DiscussionsPanelContextActions } from "~/components/AssetModal/shared/context/DiscussionsPanelContext/discussionsPanelContextTypes";
import { discussionsPanelContextDispatch } from "~/components/AssetModal/shared/context/DiscussionsPanelContext/discussionsPanelContextUtils";
import { isDevOrTestStage } from "~/swr-hooks/utils";
import { reportErrorToBugsnag } from "~/utils/ErrorUtils";
const DiscussionsPanelContext = /*#__PURE__*/ createContext(defaultDiscussionPanelContextValue);
export const DiscussionsPanelContextProvider = (param)=>{
    let { children } = param;
    const { contextValue } = useMemoizedContextProvider({
        discussionsVersionFilter: ASSET_MODAL_DISCUSSION_VERSION_FILTERS.ALL,
        discussionsFilter: ASSET_MODAL_DISCUSSION_FILTERS.OPENED,
        discussionsSort: ASSET_MODAL_DISCUSSION_SORT.DEFAULT,
        openReplies: {}
    }, discussionsPanelContextDispatch);
    const setDiscussionsFilter = useCallback((filter)=>contextValue.dispatch({
            type: DiscussionsPanelContextActions.setDiscussionsFilter,
            data: filter
        }), [
        contextValue
    ]);
    const setDiscussionsSort = useCallback((sort)=>contextValue.dispatch({
            type: DiscussionsPanelContextActions.setDiscussionsSort,
            data: sort
        }), [
        contextValue
    ]);
    const setDiscussionsVersionFilter = useCallback((filter)=>contextValue.dispatch({
            type: DiscussionsPanelContextActions.setDiscussionsVersionFilter,
            data: filter
        }), [
        contextValue
    ]);
    const setReplyingDiscussionId = useCallback((discussionId)=>contextValue.dispatch({
            type: DiscussionsPanelContextActions.setReplyingDiscussionId,
            data: discussionId
        }), [
        contextValue
    ]);
    const clearReplyingDiscussionId = useCallback(()=>contextValue.dispatch({
            type: DiscussionsPanelContextActions.clearReplyingDiscussionId
        }), [
        contextValue
    ]);
    const setRepliesOpen = useCallback((discussionId, isOpen)=>contextValue.dispatch({
            type: DiscussionsPanelContextActions.setRepliesOpen,
            data: {
                discussionId,
                isOpen
            }
        }), [
        contextValue
    ]);
    const value = useMemo(()=>({
            ...contextValue,
            setRepliesOpen,
            setDiscussionsFilter,
            setDiscussionsSort,
            setDiscussionsVersionFilter,
            setReplyingDiscussionId,
            clearReplyingDiscussionId
        }), [
        clearReplyingDiscussionId,
        contextValue,
        setRepliesOpen,
        setDiscussionsFilter,
        setDiscussionsSort,
        setDiscussionsVersionFilter,
        setReplyingDiscussionId
    ]);
    return /*#__PURE__*/ _jsx(DiscussionsPanelContext.Provider, {
        value: value,
        children: children
    });
};
export const useDiscussionPanelContext = ()=>{
    const context = useContext(DiscussionsPanelContext);
    if (context === defaultDiscussionPanelContextValue) {
        const error = "DiscussionsPanelContext used outside of DiscussionsPanelProvider";
        if (isDevOrTestStage()) {
            throw error;
        } else {
            reportErrorToBugsnag({
                error,
                context: error
            });
        }
    }
    return context;
};
export function useDiscussionPanelContextSelector(selector) {
    const context = useDiscussionPanelContext();
    return useMemoizedContextSelector(context, selector);
}
